import QueryString from "query-string";
import { all, call, put, select, takeEvery } from "redux-saga/effects";
import api from "../../utils/api";
import { errorAlert, successAlert } from "../Alerts/actions";
import {
  deleteAssessmentTemplateFailure,
  deleteAssessmentTemplateSuccess,
  getAssessmentTemplateFailure,
  getAssessmentTemplates,
  getAssessmentTemplatesFailure,
  getAssessmentTemplatesSuccess,
  getAssessmentTemplateSuccess,
  saveAssessmentTemplateFailure,
  saveAssessmentTemplateSuccess,
  toggleAssessmentModal,
  toggleTemplateLocationSettingFailure,
  toggleTemplateLocationSettingSuccess,
} from "./actions";
import {
  //
  DELETE_ASSESSMENT_TEMPLATE,
  //
  TOGGLE_TEMPLATE_LOCATION_SETTING,
  //
  GET_ASSESSMENT_TEMPLATE,
  GET_ASSESSMENT_TEMPLATES,
  SAVE_ASSESSMENT_TEMPLATE,
  TEMPLATE_TYPE_CARE_PLAN,
  TEMPLATE_TYPE_CARE_PLAN_ACTION,
  TEMPLATE_TYPE_GENERIC,
  TEMPLATE_TYPE_NEEDS,
  TEMPLATE_TYPE_RISK,
  TEMPLATE_TYPE_SAFEGUARDING,
  TEMPLATE_TYPE_INCIDENT,
  TEMPLATE_TYPE_MEDICAL_CONDITION,
  TEMPLATE_TYPE_CAPACITY,
  TEMPLATE_TYPE_DOLS,
  TEMPLATE_TYPE_DOL_DECISION,
  TEMPLATE_TYPE_CONSENT,
  TEMPLATE_TYPE_TOOL,
} from "./constants";

// Helper function to get template type query parameter
const getTemplateTypeParam = (templateType) => {
  if (templateType === TEMPLATE_TYPE_GENERIC) {
    return "generic";
  } else if (templateType === TEMPLATE_TYPE_NEEDS) {
    return "need_assessment";
  } else if (templateType === TEMPLATE_TYPE_RISK) {
    return "risk_assessment";
  } else if (templateType === TEMPLATE_TYPE_SAFEGUARDING) {
    return "safeguarding";
  } else if (templateType === TEMPLATE_TYPE_INCIDENT) {
    return "incident";
  } else if (templateType === TEMPLATE_TYPE_CARE_PLAN) {
    return "care_plan";
  } else if (templateType === TEMPLATE_TYPE_CARE_PLAN_ACTION) {
    return "care_plan_action";
  } else if (templateType === TEMPLATE_TYPE_MEDICAL_CONDITION) {
    return "medical_condition";
  } else if (templateType === TEMPLATE_TYPE_CAPACITY) {
    return "capacity_mac";
  } else if (templateType === TEMPLATE_TYPE_DOLS) {
    return "dols";
  } else if (templateType === TEMPLATE_TYPE_DOL_DECISION) {
    return "dol_decision";
  } else if (templateType === TEMPLATE_TYPE_CONSENT) {
    return "consent";
  } else if (templateType === TEMPLATE_TYPE_TOOL) {
    return "tool";
  }
  return null;
};

// Helper function to get success message based on template type
const getSuccessMessage = (templateType, action) => {
  let typeLabel = "Template";
  if (templateType === TEMPLATE_TYPE_NEEDS) {
    typeLabel = "Needs";
  } else if (templateType === TEMPLATE_TYPE_RISK) {
    typeLabel = "Risk";
  } else if (templateType === TEMPLATE_TYPE_SAFEGUARDING) {
    typeLabel = "Safeguarding";
  } else if (templateType === TEMPLATE_TYPE_INCIDENT) {
    typeLabel = "Incident";
  } else if (templateType === TEMPLATE_TYPE_CARE_PLAN) {
    typeLabel = "Care Plan";
  } else if (templateType === TEMPLATE_TYPE_CARE_PLAN_ACTION) {
    typeLabel = "Care Plan Actions";
  } else if (templateType === TEMPLATE_TYPE_MEDICAL_CONDITION) {
    typeLabel = "Medical Condition";
  } else if (templateType === TEMPLATE_TYPE_CAPACITY) {
    typeLabel = "Capacity";
  } else if (templateType === TEMPLATE_TYPE_DOLS) {
    typeLabel = "DoLS";
  } else if (templateType === TEMPLATE_TYPE_DOL_DECISION) {
    typeLabel = "Liberty (DoL)";
  } else if (templateType === TEMPLATE_TYPE_CONSENT) {
    typeLabel = "Consent";
  } else if (templateType === TEMPLATE_TYPE_GENERIC) {
    typeLabel = "Generic";
  } else if (templateType === TEMPLATE_TYPE_TOOL) {
    typeLabel = "Tool";
  }
  if (action === "save") {
    return `${typeLabel} template saved successfully!`;
  } else if (action === "delete") {
    return "Template deleted successfully!";
  }
  return "Operation completed successfully!";
};

export function* getAssessmentTemplatesSaga({ templateType, payload, onGet }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const templateTypeParam = getTemplateTypeParam(templateType);

  try {
    // Build query params - payload should be an object with locationId
    const queryParams = {
      ...payload,
    };

    // DoLS is stored as template_type='generic' + template_category='dols'
    // (like ReSPECT/DNACPR, so it surfaces via the legal-safeguarding chip and
    // the resident generic lane). Its settings tab therefore filters by
    // CATEGORY, not type — otherwise ?template_type=dols matches nothing.
    // The post-AGNI liberty decision (template 458) is stored the same way,
    // under template_category='dol_decision'.
    if (templateType === TEMPLATE_TYPE_DOLS) {
      queryParams.template_category = "dols";
    } else if (templateType === TEMPLATE_TYPE_DOL_DECISION) {
      queryParams.template_category = "dol_decision";
    } else if (templateTypeParam) {
      queryParams.template_type = templateTypeParam;
    }

    const query = QueryString.stringify(queryParams);
    const url = `/api/assessment-template?${query}`;

    const { data } = yield call(api(token).get, url);
    yield put(getAssessmentTemplatesSuccess(templateType, data ?? []));
    typeof onGet === "function" && onGet(data);
  } catch (e) {
    yield put(getAssessmentTemplatesFailure(templateType));
  }
}

export function* getAssessmentTemplateSaga({ templateType, payload, onGet }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const url = `/api/assessment-template/${payload}`;

  try {
    const { data } = yield call(api(token).get, url);
    yield put(getAssessmentTemplateSuccess(templateType, data));
    typeof onGet === "function" && onGet(data);
  } catch (e) {
    yield put(getAssessmentTemplateFailure(templateType));
  }
}

export function* saveAssessmentTemplateSaga({ templateType, payload, onSave }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const templateTypeParam = getTemplateTypeParam(templateType);

  if (!templateTypeParam) {
    yield put(saveAssessmentTemplateFailure(templateType));
    return;
  }

  const {
    location_id: locationId,
    template_id: templateId,
    data: templateData,
  } = payload;

  try {
    // Include template type in the request data
    const requestData = {
      ...templateData,
      type: templateTypeParam,
    };

    // Persist DoLS as a generic/dols form (not template_type='dols') so it keeps
    // surfacing via the legal-safeguarding chip + resident generic lane, and the
    // one-per-location restriction (keyed on category) applies. The liberty
    // decision persists the same way under its own category.
    if (templateType === TEMPLATE_TYPE_DOLS) {
      requestData.type = "generic";
      requestData.template_type = "generic";
      requestData.template_category = "dols";
    } else if (templateType === TEMPLATE_TYPE_DOL_DECISION) {
      requestData.type = "generic";
      requestData.template_type = "generic";
      requestData.template_category = "dol_decision";
    }

    const url = templateId
      ? `/api/assessment-template/edit/${templateId}`
      : `/api/assessment-template/add`;
    const method = "post";

    const { data } = yield call(api(token)[method], url, requestData);

    yield put(saveAssessmentTemplateSuccess(templateType));
    yield put(successAlert(getSuccessMessage(templateType, "save")));
    yield put(
      toggleAssessmentModal(templateType, {
        open: false,
        isEditing: false,
        template: null,
      }),
    );

    // Refresh templates list
    // yield put(
    //   getAssessmentTemplates(templateType, {
    //     location_id: locationId,
    //     template_type: templateTypeParam,
    //   }),
    // );

    typeof onSave === "function" && onSave(data);
  } catch (e) {
    yield put(saveAssessmentTemplateFailure(templateType));
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while saving template!"));
  }
}

export function* deleteAssessmentTemplateSaga({
  templateType,
  payload,
  onDelete,
}) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const templateTypeParam = getTemplateTypeParam(templateType);

  if (!templateTypeParam) {
    yield put(deleteAssessmentTemplateFailure(templateType));
    return;
  }

  const { templateId, locationId } = payload;

  try {
    const url = `/api/assessment-template/${templateId}`;
    yield call(api(token).delete, url);
    yield put(deleteAssessmentTemplateSuccess(templateType));
    yield put(successAlert(getSuccessMessage(templateType, "delete")));

    // Refresh templates list
    // yield put(
    //   getAssessmentTemplates(templateType, {
    //     location_id: locationId,
    //     template_type: templateTypeParam,
    //   }),
    // );

    typeof onDelete === "function" && onDelete();
  } catch (e) {
    yield put(deleteAssessmentTemplateFailure(templateType));
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while deleting template!"));
  }
}

export function* toggleTemplateLocationSettingSaga({
  templateType,
  payload,
  onToggle,
}) {
  const token = yield select((state) => state?.AuthReducer?.token);

  const { templateId, locationId, isEnabled } = payload;

  try {
    const url = `/api/assessment-template/location-setting/${templateId}`;
    yield call(api(token).post, url, {
      location_id: locationId,
      is_enabled: isEnabled,
    });
    yield put(toggleTemplateLocationSettingSuccess(templateType));
    yield put(
      successAlert(
        isEnabled
          ? "Template enabled for this home."
          : "Template disabled for this home.",
      ),
    );

    // Refresh the current list on success
    typeof onToggle === "function" && onToggle();
  } catch (e) {
    yield put(toggleTemplateLocationSettingFailure(templateType));
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while updating template setting!"));
  }
}

export function* getAssessmentTemplatesFlow() {
  yield takeEvery(GET_ASSESSMENT_TEMPLATES, getAssessmentTemplatesSaga);
}
export function* getAssessmentTemplateFlow() {
  yield takeEvery(GET_ASSESSMENT_TEMPLATE, getAssessmentTemplateSaga);
}
export function* saveAssessmentTemplateFlow() {
  yield takeEvery(SAVE_ASSESSMENT_TEMPLATE, saveAssessmentTemplateSaga);
}
export function* deleteAssessmentTemplateFlow() {
  yield takeEvery(DELETE_ASSESSMENT_TEMPLATE, deleteAssessmentTemplateSaga);
}
export function* toggleTemplateLocationSettingFlow() {
  yield takeEvery(
    TOGGLE_TEMPLATE_LOCATION_SETTING,
    toggleTemplateLocationSettingSaga,
  );
}

export default function* ResidentAssessmentTemplateSaga() {
  yield all([
    getAssessmentTemplatesFlow(),
    getAssessmentTemplateFlow(),
    saveAssessmentTemplateFlow(),
    deleteAssessmentTemplateFlow(),
    toggleTemplateLocationSettingFlow(),
  ]);
}
